import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useAlbumSamples } from "@bodhistreams/api/src";
import { BodhiAlbumCard, BodhiLoader } from "@bodhistreams/ui";
import { Typography, theme } from "antd";
import { useEffect, useId, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const { Title } = Typography;
const { useToken } = theme;

// Define styles for the carousel and album cards
const getStyles = (token) => ({
	carouselContainer: {
		padding: "0 40px",
		position: "relative",
		marginBottom: "60px",
		maxWidth: "1400px",
		margin: "0 auto",
	},
	slickArrow: {
		position: "absolute",
		top: "50%",
		transform: "translateY(-50%)",
		zIndex: 2,
		width: "40px",
		height: "40px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: token.colorBgElevated,
		borderRadius: "50%",
		boxShadow: token.boxShadow,
		cursor: "pointer",
		border: `1px solid ${token.colorBorder}`,
	},
});

// Add custom CSS for slick carousel
const getCustomCSS = (token) => `
  .slick-slide > div {
    height: 100%;
    margin: 0 10px;
  }
  
  .slick-list {
    margin: 0 -10px;
    padding-bottom: 20px !important;
  }
  
  .slick-track {
    display: flex !important;
  }
  
  .slick-slide {
    height: inherit !important;
  }
  
  .slick-slide > div {
    height: 100%;
  }
  
  .slick-dots li button:before {
    font-size: 12px;
    color: ${token.colorTextQuaternary};
  }
  
  .slick-dots li.slick-active button:before {
    color: ${token.colorPrimary};
  }
`;

const NextArrow = ({ onClick, token }) => {
	return (
		<button
			type="button"
			style={{
				...getStyles(token).slickArrow,
				right: "-20px",
				border: "none",
				background: "transparent",
				cursor: "pointer",
				padding: 0,
				outline: "none",
			}}
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick();
				}
			}}
		>
			<RightOutlined style={{ fontSize: "18px", color: token.colorText }} />
		</button>
	);
};

const PrevArrow = ({ onClick, token }) => {
	return (
		<button
			type="button"
			style={{
				...getStyles(token).slickArrow,
				left: "-20px",
				border: "none",
				background: "transparent",
				cursor: "pointer",
				padding: 0,
				outline: "none",
			}}
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick();
				}
			}}
		>
			<LeftOutlined style={{ fontSize: "18px", color: token.colorText }} />
		</button>
	);
};

const Collections = () => {
	const { token } = useToken();
	const uniqueId = useId();
	const { data: albumsData, isLoading: loading, error } = useAlbumSamples();
	const albums = Array.isArray(albumsData) ? albumsData : [];
	const [playingAlbum, setPlayingAlbum] = useState(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const audioRef = useRef(new Audio());
	const [albumMedia, setAlbumMedia] = useState({});

	const styles = getStyles(token);

	useEffect(() => {
		// Process sample tracks from the albums response
		const processAlbumMedia = () => {
			const mediaData = {};

			for (const album of albums) {
				if (album.id) {
					// Check if we have a sample_track
					if (album.sample_track) {
						mediaData[album.id] = 1;
					} else {
						mediaData[album.id] = album.tracks_count || 0;
					}
				}
			}

			setAlbumMedia(mediaData);
		};

		if (albums.length > 0) {
			processAlbumMedia();
		}
	}, [albums]);

	// Handle audio play/pause and cleanup
	useEffect(() => {
		const audio = audioRef.current;

		const handleEnded = () => {
			setIsPlaying(false);
			setPlayingAlbum(null);
		};

		audio.addEventListener("ended", handleEnded);

		return () => {
			audio.pause();
			audio.removeEventListener("ended", handleEnded);
		};
	}, []);

	// Play the sample track from an album
	const playRandomTrack = (albumId) => {
		// If we're already playing this album, just toggle play/pause
		if (playingAlbum === albumId) {
			if (isPlaying) {
				audioRef.current.pause();
				setIsPlaying(false);
			} else {
				audioRef.current.play();
				setIsPlaying(true);
			}
			return;
		}

		// If we were playing something else, stop it first
		if (isPlaying) {
			audioRef.current.pause();
		}

		// Find the album and get its sample track
		const album = albums.find((a) => a.id === albumId);

		if (album?.sample_track) {
			const track = album.sample_track;

			// Set new audio source and play
			audioRef.current.src = track.cdn_url;
			audioRef.current.play();

			setPlayingAlbum(albumId);
			setIsPlaying(true);
		}
	};

	if (loading && albums.length === 0) {
		return (
			<div style={{ textAlign: "center" }}>
				<BodhiLoader mode="inline" size={64} backgroundOpacity={0} />
			</div>
		);
	}

	if (error) {
		return (
			<div style={{ textAlign: "center" }}>
				<Title level={2}>Curated Collections</Title>
				<p>Unable to load collections. Please try again later.</p>
			</div>
		);
	}

	// Carousel settings
	const settings = {
		dots: true,
		infinite: true,
		speed: 500,
		slidesToShow: 5,
		slidesToScroll: 1,
		nextArrow: <NextArrow token={token} />,
		prevArrow: <PrevArrow token={token} />,
		autoplay: true,
		autoplaySpeed: 5000,
		pauseOnHover: true,
		swipeToSlide: true,
		responsive: [
			{
				breakpoint: 1600,
				settings: {
					slidesToShow: 4,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 1200,
				settings: {
					slidesToShow: 3,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 768,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 576,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
				},
			},
		],
	};

	return (
		<div id={`${uniqueId}-collections`} style={{ position: "relative" }}>
			<style>{getCustomCSS(token)}</style>
			<div style={styles.carouselContainer}>
				<Slider {...settings}>
					{albums.map((album) => (
						<div key={album.id} style={{ padding: "10px" }}>
							<BodhiAlbumCard
								album={album}
								onClick={() => playRandomTrack(album.id)}
								onPlay={
									albumMedia[album.id] > 0
										? () => playRandomTrack(album.id)
										: undefined
								}
								isCurrent={playingAlbum === album.id}
								isPlaying={isPlaying}
							/>
						</div>
					))}
				</Slider>
			</div>
		</div>
	);
};

export default Collections;
